import React from 'react';
import { lineOwner } from './shared';

/*
  What a line looks like on the screen, in the pieces every width shares.

  Pulled out of `CountEntry.jsx` on 5 September 2026, when its three layouts
  became one list. They live here rather than in the screen so that the list can
  use them without the screen and the list importing each other in a circle,
  which on this project has twice produced a completely blank grey page with
  nothing anywhere to say why.
*/

/*
  **What a quantity box shows, and it is the heart of job 10.**

  A line whose quantity is null has never been counted. A line whose quantity is
  0 was counted and the shelf was empty. **They are opposite facts and all three
  of this screen's layouts drew them the same**, as `0`, because every one of
  them rendered `item.quantity || 0`.

  Counted on the open cycle on 5 September 2026: **600 of its 2,530 lines have
  never been counted and every one showed a zero.** 78 were honest zeroes. A rep
  could not tell 600 things they still owed from 78 they had already done, which
  is why the build order calls this the single most valuable change in the job.

  A blank box now, tinted, with a question mark where there is room for one.
  Rule 9: blank is not zero, and it has to look it.
*/

export function qtyBoxValue(item) {
  return item?.quantity ?? '';
}
export function isUncounted(item) {
  return item?.quantity === null || item?.quantity === undefined;
}
// How many lines are counted of how many are on the sheet, and how many are left.
/*
  The one strip that says how far through a sheet somebody is: a thin bar, then
  "412 of 486" and "74 left". The number left is in the not-started colour,
  because it is the thing still owed.
*/
export function SheetProgress({ total, done, left, compact }) {
  const pct = total > 0 ? Math.round(done / total * 100) : 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <span style={{ display: 'block', width: compact ? 70 : 110, height: 5, borderRadius: 3, background: 'var(--border-soft)', overflow: 'hidden', flexShrink: 0 }}>
        <span style={{ display: 'block', height: '100%', width: pct + '%', background: pct === 100 ? 'var(--success)' : 'var(--info)' }} />
      </span>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
        {done + ' of ' + total}
      </span>
      {left > 0 && (
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {left + ' left'}
        </span>
      )}
    </span>
  );
}

/*
  The band across the top of a sheet several people may count at once.

  Gold while somebody else is actually in it and quiet when nobody is, because a
  standing gold band on a location that allows sharing would be a warning about
  nothing for the whole of a count.
*/
export function SharedBand({ says, live }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, borderRadius: 12, padding: '11px 14px',
      marginBottom: 14, flexShrink: 0, fontSize: 13, lineHeight: 1.5,
      background: live ? 'var(--warning-bg)' : 'var(--hover)',
      color: live ? 'var(--warning-ink)' : 'var(--text-secondary)',
    }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
      <span>{says}</span>
    </div>
  );
}

/*
  Whose count this line is, where it is not mine.

  On the approved drawing it is a small chip beside the description, "MARIA D.",
  on the one line another person had counted. **A line is a fact about a shelf
  and somebody has to be able to ask who put that number there**, which on a
  sheet two people are working was impossible from any screen.
*/
export function OwnerChip({ item, myId }) {
  const who = lineOwner(item, myId);
  if (!who) return null;
  return <span className="chip-owner" style={{ marginLeft: 8 }}>{who}</span>;
}

export function sheetProgress(items) {
  const total = items.length;
  const done  = items.filter(i => !isUncounted(i)).length;
  return { total, done, left: total - done };
}

/*
  What the Submit dialog is allowed to do, counted in one place.

  **The database has refused a sheet with a blank line on it since before this
  screen existed** and the screen did not know. There is a rule on
  `inventory_counts` called `check_count_fully_counted`, a small piece of
  database code that runs on every save and rejects the sheet while any line has
  no number on it. Count entry offered "Yes, Submit Count" anyway, on
  Michelle Chelette's Main Regional Office sheet with 75 lines nobody had been
  to, and a rep pressing it got a refusal that said nothing about which lines.

  So Submit is locked until every line has a number, and the dialog says how
  many are still blank. `zeroes` is the count of lines somebody counted and
  found nothing on, which is what the dialog shows once there is nothing blank
  left: an administrator reviewing the sheet wants to know how many shelves were
  genuinely empty.
*/
export function submitState(items) {
  const list    = items || [];
  const total   = list.length;
  const blank   = list.filter(isUncounted).length;
  const zeroes  = list.filter(i => i.quantity === 0).length;
  return { total, blank, zeroes, counted: total - blank, ready: blank === 0 && total > 0 };
}
